import { equals, pick } from 'ramda';
import * as React from 'react';
import { Route, createBrowserRouter, createRoutesFromElements } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import AttachmentWindow from 'Routes/_AttachmentWindow';
import MainLander from 'Routes/_MainLander';
import PatientRecords from 'Routes/_PatientRecords';
import { useAppSelector } from 'Store/index';
import { Dashboard, documentRoutes } from '../../routes';
import { getJWTData } from 'Utility/jwtAuthTools';
import { getEnabledDocuments, getNavigableRoutes } from 'Utility/randomUtil';
import Layout from '../Layout/index';

type FocusChangeListener = () => void;
const compareKeys: Array<keyof IJWTBody> = ['patientid', 'visitid', 'useruuid']; // Which keys to use for detecting changed JWT

const createFocusListener = (): FocusChangeListener => {
  const jwt = getJWTData();
  if (!jwt) return () => null;
  const oldValue = pick(compareKeys, jwt);
  return (): void => {
    const newValue = pick(compareKeys, jwt);
    if (!equals(oldValue, newValue)) location.replace('/');
  };
};

const MainRouter = (): React.JSX.Element => {
  const session = useAppSelector(s => s.session);
  const sections = getEnabledDocuments(session).map(d => d.name);
  const navigableSections = getNavigableRoutes(session).map(d => d.name);
  const prevPatient = !!session.data?.patientid;

  React.useEffect(() => {
    const listener = createFocusListener();
    window.addEventListener('focus', listener);
    return (): void => {
      window.removeEventListener('focus', listener);
    };
  });

  const router = React.useMemo(() => {
    return createBrowserRouter(
      createRoutesFromElements(
        <Route element={<Layout />}>
          <Route id="main" path="/" element={<Dashboard sections={sections} />} />
          <Route id="search" path="/search" element={<MainLander prevPatient={prevPatient} />} />
          <Route
            id="messaging"
            path="/messaging"
            element={<MainLander prevPatient={prevPatient} activeTab="messaging" />}
          />
          <Route id="patientRecords" path="/patientRecords" element={<PatientRecords />} />
          <Route id="attachments" path="/attachments/:id" element={<AttachmentWindow />} />

          {documentRoutes(navigableSections).map(route => (
            <Route id={route.path} key={route.path} {...route} />
          ))}
          <Route id="matchOtherPaths" path="*" element={<Dashboard sections={sections} />} />
        </Route>,
      ),
    );
  }, [sections.join(','), navigableSections.join(','), prevPatient]);

  return <RouterProvider router={router} useTransitions={false} />;
};

export default MainRouter;
